import { useState, useEffect, useCallback, useMemo } from "react";
import { graphql, useStaticQuery, withPrefix } from "gatsby";
import { queryProductsDetails } from "../utility/ecommerce";
import babelConfig from "../../babel-config.json";
import { hasEcommerce } from "../utility/ecommerce";

const formatDDLPrices = (productIds, products, config) => {
  if (products?.some((product) => product.productSellingPrice)) {
    return productIds.reduce((priceInfo, productId) => {
      const product = products.find((item) => item.key_prod === productId);
      const { productMsrpPrice, productSellingPrice, productOmnibusPrice, shopLink } =
        product || {};
      if (productSellingPrice) {
        priceInfo.set(productId, {
          sellingPrice: {
            currency: config.currency,
            value: parseFloat(productSellingPrice.replace(",", ".")),
          },
          ...(productMsrpPrice && {
            msrpPrice: {
              currency: config.currency,
              value: parseFloat(productMsrpPrice.replace(",", ".")),
            },
          }),
          ...(productOmnibusPrice && {
            omnibusPrice: {
              currency: config.currency,
              value: parseFloat(productOmnibusPrice.replace(",", ".")),
            },
          }),
          shopLink,
        });
      }
      return priceInfo;
    }, new Map());
  }
  return new Map();
};

const useProductPrices = (productIds, products) => {
  const { config } = useStaticQuery(graphql`
    {
      config: liferayGeneralConfiguration {
        currency
      }
    }
  `);

  const priceInfoFromDDL = useMemo(
    () => formatDDLPrices(productIds, products, config),
    [productIds, products, config]
  );
  const [normalizedProductIds, productIdsMap] = useMemo(() => {
    const pathPrefix = withPrefix("/");
    const normalizedProductIds = [];
    const productIdsMap = new Map();
    for (const productId of productIds) {
      const { extraInfo } = products.find((item) => item.key_prod === productId) || {};
      const normalizedProductId =
        babelConfig.domainsWithSap.includes(pathPrefix) && extraInfo?.ca4_CHG_HAIER_SAP
          ? extraInfo?.ca4_CHG_HAIER_SAP
          : productId;
      productIdsMap.set(normalizedProductId, productId);
      normalizedProductIds.push(normalizedProductId);
    }
    const inverseProductIdsMap = new Map(
      Object.entries([...productIdsMap.entries()].map(([key, value]) => [value, key]))
    );
    return [normalizedProductIds, productIdsMap, inverseProductIdsMap];
  }, [productIds, products]);
  const [priceInfo, setPriceInfo] = useState();
  const currency = config?.currency;
  const loadPrices = useCallback(async () => {
    const productsToQuery = normalizedProductIds;
    if (!productsToQuery.length) return;
    try {
      const items = await queryProductsDetails(normalizedProductIds, currency);
      const validItems = (items || []).filter((item) => item.availableForSale);
      setPriceInfo(() => {
        const validItemsReduced = validItems.reduce((res, item) => {
          res.set(productIdsMap.get(item.sku), item);
          return res;
        }, new Map(priceInfoFromDDL));
        return validItems?.length > 0 ? validItemsReduced : new Map(priceInfoFromDDL);
      });
    } catch (err) {
      console.error("Error loading prices: ", err);
      setPriceInfo(new Map(priceInfoFromDDL));
    }
  }, [normalizedProductIds, productIdsMap, currency, priceInfoFromDDL]);

  useEffect(() => {
    if (hasEcommerce) {
      loadPrices();
    } else if (priceInfoFromDDL) {
      setPriceInfo(new Map(priceInfoFromDDL));
    }
  }, [loadPrices, priceInfoFromDDL]);

  return priceInfo;
};

export default useProductPrices;
